import React, { useCallback, useEffect, useState } from "react";
import { Send, Loader2, X, Mail, CheckCircle2, Paperclip, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const LBL = {
  sopralluogo: { field: "Sopralluogo", name: "sopralluogo", pdf: "sopralluogo" },
  installazione: { field: "Installazioni", name: "installazione", pdf: "pratica installazione" },
  intervento: { field: "Interventi", name: "intervento", pdf: "intervento" },
};

export const InviaCommessaButton = ({ cid, module, allDone }) => {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [dicoIds, setDicoIds] = useState([]);

  const load = useCallback(() => {
    api.get(`/clienti/${cid}/invia-commessa/${module}`).then(({ data }) => {
      setSt(data);
      setDicoIds((data.dico || []).map((d) => d.id));
    }).catch(() => setSt(null));
  }, [cid, module]);
  useEffect(() => { load(); }, [load, allDone]);

  if (!st) return null;
  const sent = st.last_sent;
  const disabled = !st.ready;
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/clienti/${cid}/invia-commessa/${module}`, { dico_ids: dicoIds });
      toast.success(`Email inviata a ${data.to.join(", ")}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  const toggleDico = (id) => setDicoIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const problems = [
    !st.smtp_configured && "Invio email non configurato: Impostazioni → Email Aruba",
    st.recipients.length === 0 && `Nessun indirizzo "${LBL[module].field}" nella commessa ${st.commessa_nome || ""}`,
  ].filter(Boolean);

  return (
    <>
      <button onClick={() => setOpen(true)} disabled={disabled} data-testid={`invia-commessa-${module}`}
        title={disabled ? `Disponibile quando tutte le sezioni sono completate (${st.completed}/${st.total})` : "Invia PDF e link alla commessa"}
        className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: "#0F4A17" }}>
        {sent ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />} {sent ? "Reinvia alla commessa" : "Invia alla commessa"}
      </button>
      {sent && <span className="text-xs self-center" style={{ color: "var(--text-muted)" }} data-testid={`invia-commessa-last-${module}`}>{sent.external ? "Già inviato in altro modo" : `Inviata il ${fmt(sent.sent_at)}`}</span>}
      {open && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setOpen(false)}>
          <div className="card-shell w-full sm:max-w-lg bg-white" onClick={(e) => e.stopPropagation()} data-testid={`invia-commessa-modal-${module}`}>
            <div className="p-5 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
              <Mail className="w-5 h-5" style={{ color: "var(--primary)" }} />
              <h2 className="text-lg font-bold flex-1" style={{ fontFamily: "Manrope" }}>Invia {LBL[module].name} alla commessa</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-black/5" data-testid="invia-commessa-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Destinatari · {st.commessa_nome}</div>
                {st.recipients.length ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="invia-commessa-recipients">
                    {st.recipients.map((r) => <span key={r} className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: "#E8F5E9", color: "#1B5E20" }}>{r}</span>)}
                  </div>
                ) : <div style={{ color: "#B30000" }}>Nessun indirizzo configurato</div>}
                {(st.cc || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="invia-commessa-cc">
                    <span className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>CC</span>
                    {st.cc.map((r) => <span key={r} className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: "#F1F5F9", color: "#334155" }}>{r}</span>)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Contenuto</div>
                <ul className="space-y-1">
                  <li className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> PDF {LBL[module].pdf} completo</li>
                  <li className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Link condivisibile alla documentazione con foto</li>
                  {module === "sopralluogo" && <li className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Note della sezione "Modulo Sopralluogo" nel testo</li>}
                </ul>
                {st.prepared ? (
                  <div className="mt-2 text-xs p-2 rounded" style={{ background: "#E8F5E9", color: "#1B5E20" }} data-testid={`invia-commessa-prepared-${module}`}>
                    PDF ({(st.prepared.size / 1048576).toFixed(1)} MB) e link già pronti dal {new Date(st.prepared.generated_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    {st.prepared.link && <> · <a href={st.prepared.link} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid={`invia-commessa-prepared-link-${module}`}>apri link</a></>}
                  </div>
                ) : st.ready && <div className="mt-2 text-xs" style={{ color: "var(--text-muted)" }} data-testid={`invia-commessa-preparing-${module}`}>PDF e link verranno generati all'invio</div>}
              </div>
              {module === "installazione" && (
                <div data-testid="invia-commessa-dico">
                  <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Allega DiCo</div>
                  {(st.dico || []).length === 0 ? <div style={{ color: "var(--text-muted)" }}>Nessuna DiCo per questo cliente</div> : st.dico.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 py-0.5">
                      <input type="checkbox" checked={dicoIds.includes(d.id)} onChange={() => toggleDico(d.id)} className="w-4 h-4 accent-[var(--primary)]" data-testid={`invia-dico-${d.id}`} />
                      DiCo n. {d.numero || "—"}
                    </label>
                  ))}
                </div>
              )}
              {sent && <div className="text-xs p-2 rounded" style={{ background: "var(--surface-muted)" }}>{sent.external ? "Già inviato in altro modo prima dell'attivazione dell'invio dal CRM" : `Ultimo invio: ${fmt(sent.sent_at)} da ${sent.sent_by_name} a ${sent.to.join(", ")}`}</div>}
              {problems.map((p) => <div key={p} className="flex items-start gap-1.5 text-xs" style={{ color: "#B30000" }}><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {p}</div>)}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setOpen(false)} className="btn-secondary">Annulla</button>
                <button onClick={send} disabled={sending || problems.length > 0} className="btn-primary disabled:opacity-50" data-testid="invia-commessa-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sent ? "Reinvia email" : "Invia email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
